import { observable, action, toJS, computed, makeObservable, runInAction } from 'mobx'

import { io } from "socket.io-client"
import decode from 'jwt-decode'
import React from 'react'
import { socketServerEndpoint } from '../service/api'
import { toast } from 'react-toastify'


export const ConnectionState = {
    IDLE: "IDLE",
    CONNECTING: "CONNECTING",
    CONNECTED: "CONNECTED",
    RETRYING: "RETRYING",
    DISCONNECTED: "DISCONNECTED",
    FAILED: "FAILED"
}


class OnlineSessionSocketStore {

    socket = null;
    heartbeatInterval = null;
    listeners = new Map();
    lastPong = Date.now();
    currentMeetingId = null;
    isInitialConnection = true;
    userId = null

    error = null;
    hasToken = false;
    status = ConnectionState.IDLE;
    retriesFailed = false;

    retryCount = 0;
    maxRetries = 5;
    baseDelay = 1000;

    constructor () {
        this.socket = null
        this.heartbeatInterval = null
        this.listeners = new Map()
        this.lastPong = Date.now()

        this.error = null
        this.hasToken = false
        this.status = ConnectionState.IDLE
        this.retriesFailed = false

        this.retryCount = 0
        this.maxRetries = 5
        this.baseDelay = 1000

        makeObservable( this, {
            retriesFailed: observable,
            hasToken: observable,
            status: observable,
            error: observable,

            _handleReconnect: action,
            disconnect: action,
            connect: action,
            on: action,

            hasPermanentlyFailed: computed,
            getConnectionStatus: computed,
            isDisconnected: computed,
            hasValidToken: computed,
            isConnecting: computed,
            isConnected: computed,
            isRetrying: computed,
        } )
    }

    _isValidToken ( token ) {
        try {
            const decoded = decode( token )
            return decoded.exp > Date.now() / 1000 || !decoded.exp
        } catch ( err ) {
            return false
        }
    }

    /**
     * Connection Logic
     */
    async connect ( meetingId, userId, recorderToken = null, query = {} ) {
        this.currentMeetingId = meetingId

        if ( this.socket?.connected ) return this.socket

        return new Promise( ( resolve, reject ) => {
            try {
                const token = this._resolveToken( meetingId, recorderToken )
                if ( !token ) {
                    this._handleAuthFailure( "No valid token found" )
                    return reject( "No valid token" )
                }

                runInAction( () => {
                    this.status = ConnectionState.CONNECTING
                } )
                this.socket = io( socketServerEndpoint, {
                    transports: ["websocket"],
                    auth: { token },
                    query: { meeting_id: meetingId, ...query },
                    reconnection: false,
                } )

                this._setupInternalHandlers( meetingId )

                this.socket.on( "connect", async () => {

                    // if ( !this.isInitialConnection && this.userId ) {
                    //     console.log( "Auto-reconnected. Restoring room context for socket:" )
                    //     try {
                    //         await this._rejoinRoomInternal( meetingId )
                    //     } catch ( e ) {
                    //         console.error( "Silent room rejoin failed", e )
                    //     }
                    // } else {
                    //     console.log( "Initial connection established. Waiting for orchestration." )
                    //     // After the first successful connect, any future 'connect' events 
                    //     // fired by _handleReconnect are reconnections.
                    //     this.isInitialConnection = false
                    //     this.userId = userId
                    // }

                    runInAction( () => {
                        this.hasToken = true
                        this.status = ConnectionState.CONNECTED

                        this.retryCount = 0
                        this.retriesFailed = false
                        this.error = null
                    } )
                    console.log( "Starting heartbeat for socket server..." )

                    this._startHeartbeat()
                    this._resubscribeListeners()
                    resolve( this.socket )
                } )

                this.socket.on( "connect_error", ( err ) => {
                    console.error( "Connect Error called" )

                    this.status = ConnectionState.DISCONNECTED
                    this._handleAuthFailure( err.message )
                    reject( err )
                } )

            } catch ( err ) {
                this._handleAuthFailure( err.message )
                err.code = "CUS_SOCKET_CONNECTION_ERROR"
                reject( err )
            }
        } )
    }

    _resolveToken ( meetingId, recorderToken ) {
        const isAgora = navigator.userAgent.includes( "AgoraWebAgent" ) || navigator.userAgent.includes( "AgoraRecorder" )
        if ( isAgora ) return "RecordingAgent"


        const stored = sessionStorage.getItem( "meetingAccessToken" )

        if ( stored ) {
            const { token, meeting_id } = JSON.parse( stored )
            if ( meeting_id === meetingId && this._isValidToken( token ) )
                return token
            console.log( "No token for the meeting" )

        }
        return recorderToken
    }

    // async _rejoinRoomInternal ( meetingId ) {

    //     const join = () => new Promise( ( resolve ) => {
    //         this.socket.emit( 'meeting:join', {
    //             room_id: meetingId,
    //             rejoining: true,
    //             user_id: this.userId
    //         }, ( response ) => {
    //             resolve( response )
    //         } )
    //     } )

    //     // We use a raw emit here to avoid the store's public .emit logic 
    //     // which might trigger another reconnection loop.
    //     if ( meetingId && this.userId ) {
    //         await join()
    //     }

    // }

    _setupInternalHandlers () {
        this.socket.on( "disconnect", ( reason ) => {

            runInAction( () => {
                // "io client disconnect" means .disconnect() was called manually
                if ( reason === "io client disconnect" ) {
                    this.status = ConnectionState.IDLE
                    this.retriesFailed = false // Usually reset this for manual stops
                } else {
                    // All other reasons (network loss, server crash, etc.)
                    this.status = ConnectionState.DISCONNECTED
                    this.retriesFailed = true
                }
            } )

            // if ( reason !== "io client disconnect" ) {
            //     this._handleReconnect()
            // }

        } )

        this.socket.on( "pong", () => {
            this.lastPong = Date.now()
        } )
    }

    /**
     * Event Handling (Managed)
     */
    on ( eventName, callback ) {
        if ( !this.listeners.has( eventName ) ) {
            this.listeners.set( eventName, new Set() )
        }
        this.listeners.get( eventName ).add( callback )

        if ( this.socket ) {
            this.socket.on( eventName, callback )
        }

        // Return cleanup for useEffect
        return () => {
            this.listeners.get( eventName )?.delete( callback )
            this.socket?.off( eventName, callback )
        }
    }

    _resubscribeListeners () {
        this.listeners.forEach( ( callbacks, eventName ) => {
            callbacks.forEach( cb => this.socket.on( eventName, cb ) )
        } )
    }

    /**
     * Promise-wrapped Emit
     */
    async emit ( eventName, data ) {
        if ( !this.socket?.connected ) {

            if ( this.status === ConnectionState.RETRYING || this.status === ConnectionState.CONNECTING )
                return { success: false }

            if ( this.retryCount >= this.maxRetries ) {
                return toast( "Server connection is" )
            }
            // await this._handleReconnect()

            try {
                await this._waitForConnection()
            } catch ( err ) {
                return { success: false }
            }
        }



        return new Promise( ( resolve, reject ) => {

            this.socket.emit( eventName, data, ( response ) => {
                if ( response?.error ) reject( response.error )
                else resolve( response )
            } )
        } )
    }

    /**
     * Heartbeat Tracker
     */
    _startHeartbeat () {
        this._stopHeartbeat( "cleanup" )
        this.lastPong = Date.now()
        this.heartbeatInterval = setInterval( async () => {

            if ( !this.socket ) return

            const now = Date.now()
            // If we haven't heard from server in 15s
            if ( now - this.lastPong > 15000 ) {
                // console.warn( "Heartbeat timeout triggered." )
                // this._handleReconnect()
            } else if ( this.status === ConnectionState.CONNECTED ) {
                this.socket.emit( "client:ping", { ts: now } )
            }

        }, 8000 )
    }

    _waitForConnection () {
        return new Promise( ( resolve, reject ) => {
            if ( this.socket?.connected ) return resolve()
            if ( this.retriesFailed ) return reject( "Retries failed. Manual reset required." )

            // Listen for the next successful connection
            this.socket.once( "connect", () => resolve() )

            // Also listen for failure so we don't hang forever
            this.socket.once( "connect_error", ( err ) => reject( err ) )

            // Safety timeout: if it takes more than 30s to even try, reject
            setTimeout( () => reject( "Connection wait timeout" ), 30000 )
        } )
    }

    async _handleReconnect () {
        this._stopHeartbeat( "reconnecting" ) // Stop pinging while we are trying to fix the connection

        if ( this.retryCount >= this.maxRetries ) {
            runInAction( () => {
                this.status = ConnectionState.DISCONNECTED
                this.retriesFailed = true
            } )
            console.error( "Max retries reached. Manual intervention required." )
            return
        }

        runInAction( () => {
            this.status = ConnectionState.RETRYING
            console.log( "Setting retrying!" )

            this.retryCount++
        } )

        // Exponential Backoff calculation: baseDelay * 2^retryCount
        const delay = this.baseDelay * Math.pow( 2, this.retryCount )
        console.log( `Retrying in ${delay}ms (Attempt ${this.retryCount})` )

        setTimeout( () => {
            if ( this.socket ) {
                // Properly reset the existing connection attempt before starting a new one
                this.socket.disconnect().connect()
                // Note: On your socket 'connect' event listener, 
                // you should call _startHeartbeat() again to reset the cycle.
            }
        }, delay )
    }

    _stopHeartbeat ( reason ) {
        console.log( "Stopped heartbeat. Reason: ", reason )

        if ( this.heartbeatInterval ) clearInterval( this.heartbeatInterval )
    }

    _handleAuthFailure ( msg ) {
        runInAction( () => {
            this.error = msg
            if ( msg.includes( "INVALID_TOKEN" ) ) {
                this.hasToken = false
                sessionStorage.clear()
            }
            // this.status = ConnectionState.IDLE
        } )
    }

    disconnect () {
        console.log( "Disconnect called" )

        if ( this.socket ) {
            this.socket.disconnect()
            this.socket = null
        }
        this._stopHeartbeat( "disconnect called" )
    }

    get isConnecting () {
        return this.status === ConnectionState.CONNECTING
    }

    get isConnected () {
        return this.status === ConnectionState.CONNECTED
    }

    get isDisconnected () {
        return this.status === ConnectionState.DISCONNECTED
    }

    get isRetrying () {
        return this.status === ConnectionState.RETRYING
    }

    get hasPermanentlyFailed () {
        return this.retriesFailed
    }

    get getConnectionStatus () {
        return this.status
    }

    get hasValidToken () {
        return this.hasToken
    }



}

const OnlineSessionsSocketStoreInstance = new OnlineSessionSocketStore()

const OnlineSessionsSocketStoreContext = React.createContext()

export default OnlineSessionsSocketStoreInstance

export const OnlineSessionsSocketStoreProvider = ( { children } ) => {
    return (
        <OnlineSessionsSocketStoreContext.Provider value={OnlineSessionsSocketStoreInstance}>{children}</OnlineSessionsSocketStoreContext.Provider>
    )
}

/* Hook to use store in any functional component */
export const useOnlineSessionsSocketStore = () => React.useContext( OnlineSessionsSocketStoreContext )